import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingOmniImageParams {
  /**
   * 图片生成提示词,最多2500字符
   * @length 0 - 2500
   */
  prompt: string;
  /**
   * 参考图片列表,支持URL或base64格式,URL会自动转换为base64
   */
  image_list?: Array<{
    /**
     * 图片URL或base64数据
     */
    image?: string;
    /**
     * 图片URL(兼容字段)
     */
    image_url?: string;
  }>;
  /**
   * 图片分辨率,可选值: 1k, 2k
   * @values 1k | 2k | 4k
   */
  resolution?: '1k' | '2k' | '4k';
  /**
   * 生成图片数量,范围1-9
   * @default 1
   * @range 1 - 9
   */
  n?: number;
  /**
   * 图片宽高比
   */
  aspect_ratio?: string;
  /**
   * 外部任务ID
   */
  external_task_id?: string;
}

export interface KlingOmniImageResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_omni_image
 *
 * <p>Kling AI Omni Image 多模态图片生成接口，支持文本提示词和参考图片输入</p>
 * <p>API Documentation: <a href="https://docs.qingque.cn/d/home/eZQCRUy_LWt70n5Wz2sTiLV6J?identityId=2LKTadU2MSa#section=h.hhdxwtuxzvyf">https://docs.qingque.cn/d/home/eZQCRUy_LWt70n5Wz2sTiLV6J?identityId=2LKTadU2MSa#section=h.hhdxwtuxzvyf</a></p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingOmniImage } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingOmniImage({
 *   // 参数见 KlingOmniImageParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingOmniImageParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingOmniImage(
  params: KlingOmniImageParams,
): Promise<KlingOmniImageResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_omni_image',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingOmniImageResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
